import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../Icon';
import Button from '../ui/Button';
import AddToProjectPanel from './AddToProjectPanel';
import GiveLoginPanel from './GiveLoginPanel';
import { initialsOf, projectsToAdd } from './peopleView';
import { formatDateTime } from '../../utils/formatDate';
import { editPersonPath } from '../../utils/accountRoutes';
import { ROLE_BADGE_CLASS, ROLE_LABELS } from '../../utils/roles';
import type { UserRole } from '../../types/User';
import type { Person, PersonProject } from '../../types/People';
import type { Workspace } from '../../utils/workspaces';

interface PersonRowProps {
  person: Person;
  /** Add people to projects, remove them, archive and restore (admin and owner). */
  canEdit: boolean;
  /** Give a login (owner only). */
  canGiveLogin: boolean;
  /** The workspace's projects, for "Add to project". */
  projects: readonly Workspace[];
  knownTeams: readonly string[];
  busy: boolean;
  onCreateLogin: (role: UserRole, email?: string) => Promise<void>;
  onAddToProject: (projectId: string, teams: string[]) => Promise<void>;
  onRemoveFromProject: (project: PersonProject) => void;
  onArchive: () => void;
  onRestore: () => void;
}

type Panel = 'login' | 'project' | null;

/** A button or form inside the row never counts as a click on the row. */
const stopRowClick = (e: React.SyntheticEvent) => e.stopPropagation();

const PersonRow: React.FC<PersonRowProps> = ({
  person, canEdit, canGiveLogin, projects, knownTeams, busy,
  onCreateLogin, onAddToProject, onRemoveFromProject, onArchive, onRestore,
}) => {
  const [panel, setPanel] = useState<Panel>(null);
  const archived = person.archived_at !== null;
  const { login } = person;
  const contact = [person.email, person.phone].filter(Boolean).join(' / ');

  return (
    <div className={`people-row${archived ? ' people-row--archived' : ''}`}>
      <div className="people-row__main">
        <div className="um-user-avatar" aria-hidden>{initialsOf(person.name, person.email)}</div>
        <div className="people-row__info">
          <div className="um-user-name">
            {/* The row's one link: its ::after stretches over the whole row (People.css), so a click anywhere on the
                row opens the page while the buttons and chips sit above it and keep their own action. */}
            <Link className="people-row__link" to={editPersonPath(person.id)} aria-label={`Edit ${person.name}`}>{person.name}</Link>
          </div>
          {contact && <div className="um-user-email">{contact}</div>}
          <div className="people-row__chips">
            {person.projects.map(pr => (
              <span key={pr.project_id} className="people-chip">
                <span className="people-chip__label">
                  {pr.project_name}
                  {pr.teams.length > 0 && <span className="people-chip__teams"> ({pr.teams.join(', ')})</span>}
                </span>
                {canEdit && !archived && (
                  <button
                    type="button"
                    className="people-chip-remove"
                    aria-label={`Take ${person.name} off ${pr.project_name}`}
                    title="Remove from this project"
                    onClick={() => onRemoveFromProject(pr)}
                  >
                    <Icon name="close" size={11} />
                  </button>
                )}
              </span>
            ))}
          </div>
        </div>
        <div className="people-row__login">
          {login ? (
            <>
              <span className={`ds-badge people-login-badge ${ROLE_BADGE_CLASS[login.role] ?? 'ds-badge-neutral'}`}>
                {ROLE_LABELS[login.role] ?? login.role}
              </span>
              <span className="um-last-login">
                {login.last_login_at ? `Last login ${formatDateTime(login.last_login_at)}` : 'Never logged in'}
              </span>
            </>
          ) : (
            <span className="ds-badge ds-badge-neutral people-login-badge">No login</span>
          )}
        </div>
      </div>

      {(canEdit || canGiveLogin) && panel === null && (
        <div className="people-row__actions" onClick={stopRowClick}>
          {archived ? (
            canEdit && <Button variant="secondary" size="sm" disabled={busy} onClick={onRestore}>Restore</Button>
          ) : (
            <>
              {canGiveLogin && !login && <Button variant="secondary" size="sm" disabled={busy} onClick={() => setPanel('login')}>Give login</Button>}
              {canEdit && <Button variant="secondary" size="sm" disabled={busy} onClick={() => setPanel('project')}>Add to project</Button>}
              {canEdit && <Button variant="ghost" size="sm" disabled={busy} onClick={onArchive}>Archive</Button>}
            </>
          )}
        </div>
      )}

      {panel === 'login' && (
        <GiveLoginPanel
          person={person}
          onCancel={() => setPanel(null)}
          onCreate={async (role, email) => { await onCreateLogin(role, email); setPanel(null); }}
        />
      )}
      {panel === 'project' && (
        <AddToProjectPanel
          projects={projectsToAdd(projects, person)}
          knownTeams={knownTeams}
          onCancel={() => setPanel(null)}
          onAdd={async (projectId, teams) => { await onAddToProject(projectId, teams); setPanel(null); }}
        />
      )}
    </div>
  );
};

export default PersonRow;
